/**
 * Permission-aware architecture.
 *
 * Roles: standard | warehouse | admin
 * Every route/action must be gated through hasPermission(user, Permission.X)
 * or hasAnyPermission(user, [...]).
 */

export const Permission = Object.freeze({
  // Standard-user surface
  DASHBOARD_VIEW: "dashboard:view",
  ORDERS_VIEW_OWN: "orders:view_own",
  ORDERS_CREATE: "orders:create",
  ORDERS_AMEND_OWN: "orders:amend_own",
  ORDERS_CANCEL_OWN: "orders:cancel_own",

  PRODUCTS_VIEW: "products:view",
  AVAILABILITY_VIEW: "availability:view",
  PROFILE_VIEW: "profile:view",
  PROFILE_EDIT_OWN: "profile:edit_own",

  // Cross-role operational perms (both admin and warehouse in A14).
  ORDERS_VIEW_ALL: "orders:view_all",
  ORDERS_CONFIRM: "orders:confirm",
  ORDERS_DISPATCH: "orders:dispatch",
  ORDERS_MARK_DELIVERED: "orders:mark_delivered",
  ORDERS_COLLECT: "orders:collect",
  PRODUCTS_MANAGE: "products:manage",
  USERS_MANAGE: "users:manage",

  // A13: order-scoped damage creation. Admin + warehouse hold this.
  ORDER_DAMAGES_CREATE: "order.damages.create",
  // A14: read-only view of the /damages audit list.
  //      Admin + warehouse hold this; admin ALSO holds ADMIN_DAMAGES_MANAGE
  //      which is what unlocks edit + delete.
  DAMAGES_VIEW: "damages:view",

  // ── Admin module (Phase A1+) ─────────────────────────────────────
  // Note: the ADMIN_ prefix is legacy from A1 when only admin held these.
  // ADMIN_DELIVERY_MANAGE and ADMIN_COLLECTION_MANAGE are also held by
  // warehouse in A14. Not worth a rename at this point; treat the prefix
  // as a namespace, not a role gate.
  ADMIN_DASHBOARD_VIEW: "admin.dashboard.view",
  ADMIN_USERS_MANAGE: "admin.users.manage",
  ADMIN_CATEGORIES_MANAGE: "admin.categories.manage",
  ADMIN_BRANDS_MANAGE: "admin.brands.manage",
  ADMIN_PRODUCTS_MANAGE: "admin.products.manage",
  ADMIN_ADDRESSES_MANAGE: "admin.addresses.manage",
  ADMIN_DELIVERY_TIMES_MANAGE: "admin.delivery_times.manage",
  ADMIN_PUBLIC_HOLIDAYS_MANAGE: "admin.public_holidays.manage",
  ADMIN_COLLECTION_MANAGE: "admin.collection.manage",
  ADMIN_DELIVERY_MANAGE: "admin.delivery.manage",
  ADMIN_DAMAGES_MANAGE: "admin.damages.manage",
  ADMIN_REPORTS_VIEW: "admin.reports.view",
});

export const Role = Object.freeze({
  STANDARD: "standard",
  WAREHOUSE: "warehouse",
  ADMIN: "admin",
});

const STANDARD_PERMS = new Set([
  Permission.DASHBOARD_VIEW,
  Permission.ORDERS_VIEW_OWN,
  Permission.ORDERS_CREATE,
  Permission.ORDERS_AMEND_OWN,
  Permission.ORDERS_CANCEL_OWN,
  Permission.PRODUCTS_VIEW,
  Permission.AVAILABILITY_VIEW,
  Permission.PROFILE_VIEW,
  Permission.PROFILE_EDIT_OWN,
]);

// A14: warehouse is a curated set — NOT inheriting standard-user perms.
// They only see the operations surface (orders queue + delivery +
// collection + damages read-only + damage creation on QT orders).
// A14.1: extended with delivery-times + public-holidays master data CRUD.
// A14.2: added read-only Products + Availability + Reports (operational
// subset). Warehouse still cannot create orders — no ORDERS_CREATE.
const WAREHOUSE_PERMS = new Set([
  Permission.DASHBOARD_VIEW,
  Permission.PROFILE_VIEW,
  Permission.PROFILE_EDIT_OWN,
  Permission.PRODUCTS_VIEW,
  Permission.AVAILABILITY_VIEW,
  Permission.ORDERS_VIEW_ALL,
  Permission.ORDERS_CONFIRM,
  Permission.ORDERS_DISPATCH,
  Permission.ORDERS_MARK_DELIVERED,
  Permission.ORDERS_COLLECT,
  Permission.ORDER_DAMAGES_CREATE,
  Permission.DAMAGES_VIEW,
  Permission.ADMIN_DELIVERY_MANAGE,
  Permission.ADMIN_COLLECTION_MANAGE,
  Permission.ADMIN_DELIVERY_TIMES_MANAGE,
  Permission.ADMIN_PUBLIC_HOLIDAYS_MANAGE,
  Permission.ADMIN_REPORTS_VIEW,
]);

const ADMIN_ONLY_PERMS = [
  Permission.ORDERS_VIEW_ALL,
  Permission.ORDERS_CONFIRM,
  Permission.ORDERS_DISPATCH,
  Permission.ORDERS_MARK_DELIVERED,
  Permission.ORDERS_COLLECT,
  Permission.PRODUCTS_MANAGE,
  Permission.USERS_MANAGE,

  Permission.ADMIN_DASHBOARD_VIEW,
  Permission.ADMIN_USERS_MANAGE,
  Permission.ADMIN_CATEGORIES_MANAGE,
  Permission.ADMIN_BRANDS_MANAGE,
  Permission.ADMIN_PRODUCTS_MANAGE,
  Permission.ADMIN_ADDRESSES_MANAGE,
  Permission.ADMIN_DELIVERY_TIMES_MANAGE,
  Permission.ADMIN_PUBLIC_HOLIDAYS_MANAGE,
  Permission.ADMIN_COLLECTION_MANAGE,
  Permission.ADMIN_DELIVERY_MANAGE,
  Permission.ADMIN_DAMAGES_MANAGE,
  Permission.ADMIN_REPORTS_VIEW,

  Permission.ORDER_DAMAGES_CREATE,
  Permission.DAMAGES_VIEW,
];

// Admin inherits everything a standard user can do PLUS all admin-only
// permissions. This mirrors the sidebar UX (admins see the standard
// section on top and the Administration section below).
const ADMIN_PERMS = new Set([...STANDARD_PERMS, ...ADMIN_ONLY_PERMS]);

export const ROLE_PERMISSIONS = {
  [Role.STANDARD]: STANDARD_PERMS,
  [Role.WAREHOUSE]: WAREHOUSE_PERMS,
  [Role.ADMIN]: ADMIN_PERMS,
};

export function hasPermission(user, permission) {
  if (!user || !user.role) return false;
  const perms = ROLE_PERMISSIONS[user.role];
  return perms ? perms.has(permission) : false;
}

export function hasAnyPermission(user, permissions) {
  return permissions.some((p) => hasPermission(user, p));
}
